import React, { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Avatar, Box, Typography } from '@mui/material';
import SignInNudge from './SignInNudge';
import { authService } from '../services/authService';
import { buddyService, UserPublicProfile } from '../services/buddyService';
import { storeReferrer } from '../utils/invite';

/** /invite/:inviterUserId — "X invited you", then the normal sign-in / register card. */
const InviteLanding: React.FC = () => {
  const { inviterUserId = '' } = useParams();
  const [signedIn, setSignedIn] = useState(() => authService.isAuthenticated());
  const [inviter, setInviter] = useState<UserPublicProfile | null>(null);

  useEffect(() => authService.subscribe(() => setSignedIn(authService.isAuthenticated())), []);

  useEffect(() => {
    if (!inviterUserId) return;
    // Remember who invited this visitor until they register (redeemed by OnboardingGate).
    storeReferrer(inviterUserId);
    let cancelled = false;
    buddyService.getUserProfile(inviterUserId)
      .then(p => { if (!cancelled) setInviter(p); })
      .catch(() => { if (!cancelled) setInviter(null); });
    return () => { cancelled = true; };
  }, [inviterUserId]);

  // Already a member: just take them to the inviter's profile.
  if (signedIn) return <Navigate to={inviterUserId ? `/u/${inviterUserId}` : '/feed'} replace />;

  const name = inviter?.displayName;

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', textAlign: 'center', pt: { xs: 1, sm: 4 } }}>
      {inviter && (
        <Avatar src={inviter.profilePhotoUrl ?? undefined} sx={{ width: 72, height: 72, mx: 'auto', mb: 2, bgcolor: 'primary.main', fontWeight: 700 }}>
          {!inviter.profilePhotoUrl && (name?.[0]?.toUpperCase() ?? '?')}
        </Avatar>
      )}
      <Typography variant="h4" sx={{ fontWeight: 800 }}>
        {name ? `${name} invited you to dive in` : "You're invited to dive in"}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
        New Achievement Scuba is where divers connect — share dives, find buddies, join clubs, and plan trips together.
      </Typography>
      <SignInNudge
        feature="join the community"
        description={name ? `Create a free account and you'll start out following ${name}.` : undefined}
        onSignedIn={() => authService.getUser()}
      />
    </Box>
  );
};

export default InviteLanding;
